/**
 * Where this portal lives, as a string both the server and the browser agree on.
 *
 * Client components kept building absolute links with
 * `typeof window !== 'undefined' ? window.location.origin : ''`. That is a
 * server/client branch inside render: the HTML goes out with `return=` empty,
 * the browser renders `return=https://…`, and React reports a hydration
 * mismatch and drops the attribute it had already sent ("This won't be patched
 * up" — so the link stays broken, not just noisy).
 *
 * `NEXT_PUBLIC_BASE_URL` is inlined at build time and therefore identical on
 * both sides, which is what makes the render deterministic. The window is only
 * a fallback for a deployment that forgot to set it; see {@link useOrigin} for
 * a variant that stays deterministic even then.
 */
const stripSlash = (u: string) => u.replace(/\/+$/, '');

export function publicOrigin(): string {
  const env = process.env.NEXT_PUBLIC_BASE_URL;
  if (env) return stripSlash(env);
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/**
 * The portal's public origin, absolute and valid on the SERVER — `robots.txt`, the
 * sitemap and every canonical URL need a host, and {@link publicOrigin} answers with
 * an empty string there by design (it has no window to fall back on).
 *
 * `NEXT_PUBLIC_BASE_URL` is authoritative. Without it the fleet convention
 * `https://stories.<parent-domain>` is correct on every stand, and a bare local
 * checkout falls through to the dev port.
 */
export function publicSiteUrl(): string {
  const env = process.env.NEXT_PUBLIC_BASE_URL?.trim();
  if (env) return stripSlash(env);

  const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim() || process.env.OWLCAT_PARENT_DOMAIN?.trim();
  if (parent) return `https://stories.${parent}`;

  return 'http://localhost:3000';
}

/**
 * An accounts `/profile` deep link that comes back to where the user was.
 *
 * Three components build this URL (the header, the profile control, the waiting
 * note) and each had its own copy of the origin branch.
 */
export function accountsProfileUrl(opts: {
  locale: string;
  /** Path to return to, e.g. `/ru/stories/abc`. Omitted → the portal root. */
  returnPath?: string;
  app?: string;
}): string {
  const accounts = stripSlash(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200');
  const origin = publicOrigin();
  const params = new URLSearchParams({
    lang: opts.locale,
    app: opts.app ?? 'portal',
    return: origin ? `${origin}${opts.returnPath ?? ''}` : '',
  });
  return `${accounts}/profile?${params.toString()}`;
}
